import { useCallback, useEffect, useState } from "react";
import { RefreshCw, ShieldCheck, ShieldOff, Plus } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, fmtTime, todayKey, STATUS_LABEL } from "@/lib/format";
import { DemoBadge, ErrorBox, PageHeader, Spinner } from "@/components/Bits";

const RUN_TONE = { success: "text-[#B7F52A]", failed: "text-red-300", disabled: "text-amber-300", running: "text-slate-300" };
const RUN_LABEL = { success: "Réussie", failed: "Échec", disabled: "Désactivée", running: "En cours" };

const VERDICT = { ok: ["Fonctionne", "text-[#B7F52A]"], plan: ["Forfait supérieur requis", "text-amber-300"], rate_limited: ["Limite de requêtes atteinte", "text-amber-300"], error: ["Erreur / indisponible", "text-red-300"] };

function Diagnostic({ data, reload }) {
  const d = data.diagnostic;
  const run = async () => {
    try { const { data: r } = await api.post("/admin/provider/diagnose"); toast.info(`Test lancé (environ ${Math.ceil(r.eta_seconds / 60)} min, pour respecter la limite de requêtes).`); setTimeout(reload, r.eta_seconds * 1000 + 3000); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="mpl-card space-y-3 p-4" data-testid="provider-diagnostic">
      <div className="flex items-center justify-between gap-3">
        <div><p className="text-sm font-bold">Test des accès BALLDONTLIE</p>
          <p className="text-xs text-slate-400">{data.team_count} équipes NBA · {data.player_count} joueurs en cache{d?.checked_at ? ` · testé le ${fmtDateTime(d.checked_at)}` : ""}</p></div>
        <button onClick={run} disabled={d?.running} className="mpl-btn-ghost shrink-0 text-xs" data-testid="provider-diagnose-button">{d?.running ? "Test en cours…" : "Tester les accès"}</button>
      </div>
      {d?.results?.map((r) => (
        <div key={r.endpoint} className="flex items-center justify-between gap-2 text-xs" data-testid={`diag-${r.endpoint}`}>
          <span><span className="text-white">{r.label}</span> <span className="font-mono-num text-slate-500">{r.endpoint}</span></span>
          <span className={`shrink-0 font-semibold ${VERDICT[r.verdict][1]}`}>{VERDICT[r.verdict][0]} ({r.http ?? "—"})</span>
        </div>
      ))}
    </section>
  );
}

function OrganizerInvites() {
  const [list, setList] = useState([]);
  const [link, setLink] = useState("");
  const load = useCallback(() => { api.get("/admin/organizer-invitations").then(({ data }) => setList(data)); }, []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    try { const { data } = await api.post("/admin/organizer-invitations", { expires_in_hours: 168, max_uses: 1 }); setLink(data.link); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const revoke = async (id) => { await api.delete(`/admin/organizer-invitations/${id}`); load(); };
  return (
    <section className="mpl-card space-y-3 p-4" data-testid="organizer-invites">
      <div className="flex items-center justify-between gap-3">
        <div><p className="text-sm font-bold">Invitations organisateur</p><p className="text-xs text-slate-400">Lien à usage unique (7 jours) pour créer le compte d'une personne qui fondera sa propre ligue.</p></div>
        <button onClick={create} className="mpl-btn-primary shrink-0" data-testid="organizer-invite-create"><Plus size={14} /> Créer</button>
      </div>
      {link && <input readOnly value={link} className="mpl-input font-mono-num text-xs" data-testid="organizer-invite-link" onFocus={(e) => e.target.select()} />}
      {list.map((i) => (
        <div key={i.id} className="flex items-center justify-between text-xs"><span className="text-slate-400">{fmtDateTime(i.created_at)} · {i.uses}/{i.max_uses} · {i.state}</span>
          {i.state === "active" && <button onClick={() => revoke(i.id)} className="text-red-300" data-testid={`organizer-invite-revoke-${i.id}`}>Révoquer</button>}</div>
      ))}
    </section>
  );
}

function DemoGames({ onChange }) {
  const { tz } = useAuth();
  const [games, setGames] = useState(null);
  const load = useCallback(() => { api.get("/admin/demo/games", { params: { date: todayKey() } }).then(({ data }) => setGames(data)); }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (g, action, winner) => {
    try { await api.post(`/admin/demo/games/${g.id}`, { action, winner, minutes: 10 }); toast.success("Action appliquée"); load(); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const create = async () => {
    try { await api.post("/admin/demo/games", { minutes: 3 }); toast.success("Match test créé (début dans 3 min)"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const B = ({ g, a, w, children }) => <button onClick={() => act(g, a, w)} data-testid={`demo-${a}${w ? `-${w}` : ""}-${g.id}`} className="rounded-full border border-white/10 px-2.5 py-1 text-[11px] font-semibold hover:bg-white/5">{children}</button>;
  return (
    <section className="mpl-card p-4" data-testid="demo-controls">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-bold">Outils de démonstration <DemoBadge /></h2>
        <button onClick={create} className="mpl-btn-ghost px-3 py-1.5 text-xs" data-testid="demo-create-game"><Plus size={14} /> Match test</button>
      </div>
      <p className="mb-3 text-xs text-slate-500">Matchs fictifs d'aujourd'hui. Ces actions simulent le fournisseur pour tester verrouillage, points, séries, annulations et corrections.</p>
      {!games ? <Spinner /> : games.map((g) => (
        <div key={g.id} className="border-t border-white/[0.05] py-3" data-testid={`demo-game-${g.id}`}>
          <p className="text-sm font-semibold">{g.away.abbr} @ {g.home.abbr} · {fmtTime(g.start_time, tz)} · <span className="text-slate-400">{STATUS_LABEL[g.status]}</span>{g.winner && <span className="text-[#B7F52A]"> · gagnant {g[g.winner].abbr}</span>}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <B g={g} a="start">Démarrer</B>
            {g.status !== "final" ? <><B g={g} a="finish" w="home">Victoire {g.home.abbr}</B><B g={g} a="finish" w="away">Victoire {g.away.abbr}</B></>
              : <><B g={g} a="correct" w="home">Corriger → {g.home.abbr}</B><B g={g} a="correct" w="away">Corriger → {g.away.abbr}</B></>}
            <B g={g} a="cancel">Annuler</B><B g={g} a="postpone">Reporter (+1 j)</B><B g={g} a="reschedule">Reprogrammer (+10 min)</B>
          </div>
        </div>
      ))}
    </section>
  );
}

export default function AdminSync() {
  const { tz } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get("/admin/sync").then(({ data }) => setData(data)).catch((e) => setError(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    setBusy(true);
    try { const { data: r } = await api.post("/admin/sync/run"); toast[r.status === "success" ? "success" : "error"](`Synchronisation : ${RUN_LABEL[r.status]}${r.error ? ` (${r.error})` : ""}`); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const outage = async (enabled) => { await api.post("/admin/demo/outage", { enabled }); load(); };

  if (error) return <ErrorBox message={error} />;
  if (!data) return <Spinner />;
  const real = data.real_provider_status;
  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Administration" title="Synchronisation"
        action={<button onClick={run} disabled={busy} className="mpl-btn-primary shrink-0" data-testid="admin-sync-run"><RefreshCw size={15} className={busy ? "animate-spin" : ""} /> Synchroniser</button>} />
      <section className="grid gap-3 sm:grid-cols-2">
        <div className="mpl-card p-4">
          <p className="mpl-eyebrow">Source active</p>
          <p className="mt-1 font-bold" data-testid="admin-sync-provider">{data.provider_label}</p>
          <p className="mt-1 text-xs text-slate-400">Dernière synchronisation réussie : <span data-testid="admin-sync-status-badge" className="text-white">{data.last_success_at ? fmtDateTime(data.last_success_at, tz) : "aucune"}</span></p>
          <p className="mt-2 text-xs text-slate-500">{Object.entries(data.game_counts).map(([k, v]) => `${STATUS_LABEL[k]} : ${v}`).join(" · ")}</p>
        </div>
        <div className="mpl-card p-4" data-testid="admin-real-provider">
          <p className="mpl-eyebrow">Données NBA réelles (BALLDONTLIE)</p>
          <p className={`mt-1 flex items-center gap-2 font-bold ${real.configured ? "text-[#B7F52A]" : "text-amber-300"}`}>
            {real.configured ? <ShieldCheck size={16} /> : <ShieldOff size={16} />} {real.configured ? "Configuré" : "Désactivé"}
          </p>
          <p className="mt-1 text-xs text-slate-400">{real.reason}</p>
        </div>
      </section>
      <OrganizerInvites />
      <Diagnostic data={data} reload={load} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-400" data-testid="admin-capabilities">Statistiques joueurs (pronostics sur les joueurs) : {data.capabilities?.player_stats ? "disponibles" : "non incluses dans le forfait actuel de la clé"}.</p>
        <button data-testid="admin-capabilities-check" className="mpl-btn-ghost text-xs" onClick={async () => {
          try { const { data: c } = await api.post("/admin/capabilities/check"); toast[c.player_stats ? "success" : "info"](c.player_stats ? "Statistiques joueurs disponibles : questions activées." : "Toujours non incluses dans le forfait."); load(); } catch (e) { toast.error(errMsg(e)); }
        }}>Vérifier maintenant</button>
      </div>
      {(
        <section className="mpl-card flex items-center justify-between gap-4 p-4">
          <div><p className="text-sm font-bold">Simuler une panne du fournisseur</p><p className="text-xs text-slate-400">Les synchronisations échoueront ; les données existantes restent affichées.</p></div>
          <Switch checked={data.demo_outage} onCheckedChange={outage} data-testid="demo-outage-switch" />
        </section>
      )}
      <p className="text-xs text-amber-300">Outils de test : ils agissent uniquement sur les matchs fictifs des ligues de test, jamais sur les vrais matchs NBA.</p>
      <DemoGames onChange={load} />
      <section>
        <h2 className="mb-3 text-sm font-bold">Journal des synchronisations</h2>
        <div className="mpl-card divide-y divide-white/[0.05]" data-testid="admin-sync-runs">
          {data.runs.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-xs">
              <span className="text-slate-400">{fmtDateTime(r.started_at, tz)} · {r.trigger.split(":")[0]}</span>
              <span className="truncate text-slate-500">{r.status === "success" ? `${r.fetched ?? 0} lus · ${r.created} créés · ${r.updated} maj · ${r.results} résultats` : r.error}</span>
              <span className={`shrink-0 font-bold ${RUN_TONE[r.status]}`}>{RUN_LABEL[r.status]}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
